import React from 'react';
import { createPortal } from 'react-dom';
import { TeeButton } from './tee-button';
import { IconX } from '@tabler/icons-react';

export interface TeeConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'destructive';
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  adminName?: string;
  /** Extra content under the description, e.g. a choice the confirm depends on. */
  children?: React.ReactNode;
  confirmDisabled?: boolean;
}

export const TeeConfirmationDialog = React.forwardRef<
  HTMLDivElement,
  TeeConfirmationDialogProps
>(
  (
    {
      isOpen,
      title,
      description,
      confirmText = 'Yes',
      cancelText = 'No',
      variant = 'default',
      onConfirm,
      onCancel,
      isLoading = false,
      adminName,
      children,
      confirmDisabled = false,
    },
    ref
  ) => {
    const titleId = React.useId();

    React.useEffect(() => {
      if (!isOpen) return;
      const handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onCancel();
      };
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onCancel]);

    if (!isOpen) return null;

    return createPortal(
      <div
        ref={ref}
        // z-[80] and pointer-events-auto, not the stock z-50.
        //
        // This backdrop is portalled to document.body, but is frequently
        // opened from inside a Sheet (box-os/src/components/ui/sheet.tsx),
        // which is z-[70] and creates its own stacking context — so a z-50
        // body portal painted BEHIND it. That is how the Calendar
        // "Delete Block" dialog (BlockDetailSheet) ended up hidden behind
        // the block panel it was opened from.
        //
        // z-[80] sits above Sheet / UserMenu (z-[70]) but below the
        // floating menus at z-[9000] (select.tsx, tee-dropdown-list.tsx), so
        // a TeeDropDownList / Select rendered inside a dialog's children
        // still opens on top of the dialog, same as before.
        //
        // Radix's modal Sheet also sets `pointer-events: none` on <body>
        // while open, which this portal would otherwise inherit (it is a
        // sibling of the Sheet's own portal, not a descendant) — so clicks
        // fell through to the Sheet's overlay underneath and dismissed the
        // sheet instead of running onConfirm/onCancel. pointer-events-auto
        // opts this portal back in, same pattern as select.tsx.
        className="fixed inset-0 z-[80] pointer-events-auto flex items-center justify-center bg-black/50"
        onClick={onCancel}
      >
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          className="bg-[var(--background-page-content)] rounded-[10px] shadow-lg border border-[var(--border-default)] max-w-lg w-full mx-4 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col items-start gap-[var(--spacing-xl)] px-[var(--spacing-l)] pt-[var(--spacing-xl)] pb-[var(--spacing-2xl)]">
            <div className="flex justify-between items-center self-stretch">
              <h2 id={titleId} className="text-heading-h3 text-[var(--text-content-sub-title)] font-normal">
                {title}
              </h2>

              <TeeButton
                variant="ghost"
                size="s"
                onClick={onCancel}
                aria-label="Close"
              >
                <IconX size={24} className="text-[var(--text-content-sub-title)]" />
              </TeeButton>
            </div>

            <div className="flex flex-col gap-3">
              {description && (
                <p className="text-body-m text-[var(--text-content-sub-title)] font-medium">
                  {description}
                </p>
              )}
              {adminName && (
                <p className="text-heading-h5 text-[var(--text-content-title)] font-semibold">
                  {adminName}
                </p>
              )}
              {children}
            </div>
          </div>

          <div className="flex gap-3 justify-end px-6 py-6 border-t border-[var(--border-default)]">
            <TeeButton
              variant="secondary"
              size="s"
              onClick={onCancel}
              disabled={isLoading}
              className="w-full md:w-auto md:min-w-[120px]"
            >
              {cancelText}
            </TeeButton>

            <TeeButton
              variant={variant === 'destructive' ? 'destructive' : 'primary'}
              size="s"
              onClick={onConfirm}
              disabled={isLoading || confirmDisabled}
              className="w-full md:w-auto md:min-w-[120px]"
            >
              {isLoading ? 'Loading...' : confirmText}
            </TeeButton>
          </div>
        </div>
      </div>,
      document.body
    );
  }
);

TeeConfirmationDialog.displayName = 'TeeConfirmationDialog';

export default TeeConfirmationDialog;
